<template>
  <app-modal
    ref="modal"
    v-bind="$attrs"
    class="modal-container"
    content-class="modal-full"
    title="VNコネクト一括設定"
  >
    <template v-slot:body="{ cancel }">
      <validation-observer v-slot="{ handleSubmit }" as="div">
        <v-form novalidate @submit.stop.prevent="handleSubmit(submit)">
          <!-- BODY -->
          <v-card-text class="pa-0">
            <div class="modal-body">
              <v-container fluid>
                <!-- ファイル種別 -->
                <v-row>
                  <v-col>
                    <h3>ファイル種別</h3>
                    <v-radio-group
                      v-model="form.fileType"
                      hide-details
                      density="comfortable"
                    >
                      <v-radio
                        v-for="option in fileTypeOptions"
                        :key="option.text"
                        :label="option.text"
                        :value="option.value"
                      />
                    </v-radio-group>
                  </v-col>
                </v-row>

                <file-upload class="mb-3" ext=".csv" @input="fileCheck" />

                <div v-if="showCsvSuccess">
                  <app-table
                    primary-key="enumber"
                    :fields="csvCheckSuccess.fields"
                    :items="csvCheckSuccess.items"
                    :td-class="cellClass"
                    :perPage="-1"
                    :fixed="true"
                    :tr-class="() => undefined"
                  >
                    <!-- サイト名 -->
                    <template #cell(siteName)="data">
                      {{ $filter.emptyTo(data.item.siteName, "-") }}
                    </template>

                    <!-- VLAN -->
                    <template #cell(vlan)="data">
                      <div class="text-right">
                        <span v-if="data.item.vlanIdType === 'SPECIFIED'">
                          {{ data.item.vlanId }}
                        </span>
                        <span v-else>
                          {{
                            $filter.enumTo(data.item.vlanIdType, "vlanIdType")
                          }}
                        </span>
                      </div>
                    </template>

                    <!-- 品目 -->
                    <template #cell(bandwidthType)="data">
                      {{
                        $filter.enumTo(
                          data.item.bandwidthType,
                          "vnConBandwidthType"
                        )
                      }}
                    </template>

                    <!-- 指定帯域 -->
                    <template #cell(bandwidth)="data">
                      <template v-if="data.item.bandwidth">
                        {{ $filter.bandwidthTo(data.item.bandwidth) }}
                      </template>
                      <template v-else>-</template>
                    </template>

                    <!-- QoS設定 -->
                    <template #cell(qos)="data">
                      <span v-if="data.item.qos">
                        {{ $filter.enumTo(data.item.qos.isQoS, "on_off") }}
                      </span>
                      <span v-else>-</span>
                    </template>

                    <!-- 備考 -->
                    <template #cell(description)="data">
                      {{ $filter.emptyTo(data.item.description, "-") }}
                    </template>

                    <!-- VLAN制御 -->
                    <template #cell(vlanControl)="data">
                      {{ $filter.enumTo(data.item.l2.isVlanControl, "on_off") }}
                    </template>

                    <!-- 割当帯域パターン -->
                    <template #cell(qosPattern)="data">
                      <span v-if="data.item.qos">
                        {{
                          $filter.enumTo(
                            data.item.qos.qosBandControlPattern,
                            "vnConControlPattern"
                          )
                        }}
                      </span>
                      <span v-else>-</span>
                    </template>

                    <!-- DHCPプライマリサーバ -->
                    <template #cell(primaryAddress)="data">
                      <span v-if="data.item.l3.dhcpRelay">
                        {{ data.item.l3.dhcpRelay.primaryAddress }}
                      </span>
                      <span v-else>-</span>
                    </template>

                    <!-- DHCPセカンダリサーバ -->
                    <template #cell(secondaryAddress)="data">
                      <span v-if="data.item.l3.dhcpRelay">
                        {{ data.item.l3.dhcpRelay.secondaryAddress }}
                      </span>
                      <span v-else>-</span>
                    </template>

                    <!-- WANアドレス -->
                    <template #cell(wanAddress)="data">
                      {{ data.item.l3.wanAddress }}
                    </template>

                    <!-- AS番号 -->
                    <template #cell(asNumber)="data">
                      <div class="text-right">
                        {{ data.item.l3.bgp4.asNumber }}
                      </div>
                    </template>

                    <!-- ピアIPアドレス -->
                    <template #cell(peerIpAddress)="data">
                      {{ data.item.l3.bgp4.peerIpAddress }}
                    </template>

                    <!-- MD5キー -->
                    <template #cell(md5Key)="data">
                      {{ $filter.emptyTo(data.item.l3.bgp4.md5Key, "-") }}
                    </template>

                    <!-- MED -->
                    <template #cell(med)="data">
                      {{ $filter.enumTo(data.item.l3.bgp4.med, "med") }}
                    </template>

                    <!-- Local Preference -->
                    <template #cell(localPreference)="data">
                      {{
                        $filter.enumTo(
                          data.item.l3.bgp4.localPreference,
                          "localPreference"
                        )
                      }}
                    </template>

                    <!-- 経路集約 -->
                    <template #cell(routeAggregation)="data">
                      {{ data.item.l3.bgp4.routeAggregation }}
                    </template>

                    <!-- 経路集約アドレス1 -->
                    <template #cell(routeAggregationAddress1)="data">
                      <span
                        v-if="data.item.l3.bgp4.routeAggregation === 'CUSTOM'"
                      >
                        {{
                          $filter.emptyTo(
                            data.item.l3.bgp4.routeAggregationAddressList[0],
                            "-"
                          )
                        }}
                      </span>
                      <span v-else>-</span>
                    </template>

                    <!-- 経路集約アドレス2 -->
                    <template #cell(routeAggregationAddress2)="data">
                      <span
                        v-if="data.item.l3.bgp4.routeAggregation === 'CUSTOM'"
                      >
                        {{
                          $filter.emptyTo(
                            data.item.l3.bgp4.routeAggregationAddressList[1],
                            "-"
                          )
                        }}
                      </span>
                      <span v-else>-</span>
                    </template>

                    <!-- 経路集約アドレス3 -->
                    <template #cell(routeAggregationAddress3)="data">
                      <span
                        v-if="data.item.l3.bgp4.routeAggregation === 'CUSTOM'"
                      >
                        {{
                          $filter.emptyTo(
                            data.item.l3.bgp4.routeAggregationAddressList[2],
                            "-"
                          )
                        }}
                      </span>
                      <span v-else>-</span>
                    </template>

                    <!-- 経路集約アドレス4 -->
                    <template #cell(routeAggregationAddress4)="data">
                      <span
                        v-if="data.item.l3.bgp4.routeAggregation === 'CUSTOM'"
                      >
                        {{
                          $filter.emptyTo(
                            data.item.l3.bgp4.routeAggregationAddressList[3],
                            "-"
                          )
                        }}
                      </span>
                      <span v-else>-</span>
                    </template>

                    <!-- 経路集約アドレス5 -->
                    <template #cell(routeAggregationAddress5)="data">
                      <span
                        v-if="data.item.l3.bgp4.routeAggregation === 'CUSTOM'"
                      >
                        {{
                          $filter.emptyTo(
                            data.item.l3.bgp4.routeAggregationAddressList[4],
                            "-"
                          )
                        }}
                      </span>
                      <span v-else>-</span>
                    </template>

                    <!-- LANアドレス1 -->
                    <template #cell(lanAddress1)="data">
                      {{
                        data.item.l3.static[0]
                          ? data.item.l3.static[0].lanAddress
                          : "-"
                      }}
                    </template>

                    <!-- ネクストホップアドレス1 -->
                    <template #cell(nextHopAddress1)="data">
                      {{
                        data.item.l3.static[0]
                          ? data.item.l3.static[0].nextHopAddress
                          : "-"
                      }}
                    </template>

                    <!-- LANアドレス2 -->
                    <template #cell(lanAddress2)="data">
                      {{
                        data.item.l3.static[1]
                          ? data.item.l3.static[1].lanAddress
                          : "-"
                      }}
                    </template>

                    <!-- ネクストホップアドレス2 -->
                    <template #cell(nextHopAddress2)="data">
                      {{
                        data.item.l3.static[1]
                          ? data.item.l3.static[1].nextHopAddress
                          : "-"
                      }}
                    </template>

                    <!-- LANアドレス3 -->
                    <template #cell(lanAddress3)="data">
                      {{
                        data.item.l3.static[2]
                          ? data.item.l3.static[2].lanAddress
                          : "-"
                      }}
                    </template>

                    <!-- ネクストホップアドレス3 -->
                    <template #cell(nextHopAddress3)="data">
                      {{
                        data.item.l3.static[2]
                          ? data.item.l3.static[2].nextHopAddress
                          : "-"
                      }}
                    </template>

                    <!-- LANアドレス4 -->
                    <template #cell(lanAddress4)="data">
                      {{
                        data.item.l3.static[3]
                          ? data.item.l3.static[3].lanAddress
                          : "-"
                      }}
                    </template>

                    <!-- ネクストホップアドレス4 -->
                    <template #cell(nextHopAddress4)="data">
                      {{
                        data.item.l3.static[3]
                          ? data.item.l3.static[3].nextHopAddress
                          : "-"
                      }}
                    </template>

                    <!-- LANアドレス5 -->
                    <template #cell(lanAddress5)="data">
                      {{
                        data.item.l3.static[4]
                          ? data.item.l3.static[4].lanAddress
                          : "-"
                      }}
                    </template>

                    <!-- ネクストホップアドレス5 -->
                    <template #cell(nextHopAddress5)="data">
                      {{
                        data.item.l3.static[4]
                          ? data.item.l3.static[4].nextHopAddress
                          : "-"
                      }}
                    </template>
                  </app-table>
                </div>

                <div v-if="showCsvError">
                  <app-table
                    primary-key="rowNumber"
                    :fields="csvCheckError.fields"
                    :items="csvCheckError.items"
                    :perPage="-1"
                    :fixed="true"
                    :tr-class="() => undefined"
                  >
                    <template #cell(error)="data">
                      <ul class="my-3">
                        <li
                          v-for="(e, i) in data.item.checkErrorMessageList"
                          :key="i"
                        >
                          {{ e }}
                        </li>
                      </ul>
                    </template>
                  </app-table>
                </div>
              </v-container>
            </div>
            <v-alert class="mb-4" v-if="!!alertMsg" :text="alertMsg" />
          </v-card-text>

          <v-divider></v-divider>
          <!-- FOOTER -->
          <v-card-actions>
            <v-list-item class="w-100 pa-1">
              <template v-slot:append>
                <v-btn
                  type="submit"
                  color="primary"
                  variant="flat"
                  v-if="showCsvSuccess"
                  >追加</v-btn
                >
                <v-btn @click="cancel" variant="outlined" color="primary"
                  >キャンセル</v-btn
                >
              </template>
            </v-list-item>
          </v-card-actions>
        </v-form>
      </validation-observer>
    </template>
  </app-modal>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import { VueFinalModal } from "vue-final-modal";
import type {
  CheckError,
  CsvCheckSuccessResponse,
  VNConnectCsvCheck,
} from "@/apis/VNConnectApi";
import Encoding from "encoding-japanese";
import type { AppTableHeader } from "@/components/AppTable.vue";

export default defineComponent({
  name: "VNConnectLumpRegister",
  data() {
    return {
      showCsvSuccess: false,
      showCsvError: false,
      alertMsg: null as string | null,
      form: {
        fileType: "L2VN" as "L2VN" | "L3VN_STATIC" | "L3VN_BGP4",
      },
      fileTypeOptions: [
        { text: "L2VN", value: "L2VN" },
        { text: "L3VN(Static)", value: "L3VN_STATIC" },
        { text: "L3VN(BGP)", value: "L3VN_BGP4" },
      ],
      csvCheckSuccess: {
        fields: vnL2Fields as AppTableHeader<VNConnectCsvCheck>[],
        items: [] as CsvCheckSuccessResponse[],
      },
      csvCheckError: {
        fields: [
          {
            key: "rowNumber",
            title: "行番号",
            width: "140px",
            align: "center",
          },
          { key: "error", title: "エラー内容", width: "300px" },
        ] as AppTableHeader<CheckError>[],
        items: [] as CheckError[],
      },
      csvCheckResponse: {} as VNConnectCsvCheck,
    };
  },
  methods: {
    async fileCheck(file: File | null) {
      if (!file) {
        this.showCsvSuccess = false;
        this.showCsvError = false;
        this.alertMsg = this.$filter.message("csv_no_select");
        return;
      }
      if (file.size === 0) {
        this.alertMsg = this.$filter.message("file_no_size");
        return;
      }
      if (!file.name.endsWith("csv")) {
        this.alertMsg = this.$filter.message("file_invalid_format");
        return;
      }

      const fileBuffer = await this.$service.file.readAsArrayBuffer(file);
      const encode = Encoding.detect(new Uint8Array(fileBuffer));
      if (encode !== "UTF8") {
        this.alertMsg = this.$filter.message("no_utf8_data");
        return;
      }

      this.alertMsg = null;

      // VNコネクト一括登録情報の登録チェックAPI呼び出し処理
      this.csvCheckResponse = await this.$api.vNConnect.checkVNConnectCsv(
        this.form.fileType,
        file.name,
        new TextDecoder().decode(fileBuffer)
      );

      switch (this.form.fileType) {
        case "L2VN":
          this.csvCheckSuccess.fields = vnL2Fields;
          break;
        case "L3VN_STATIC":
          this.csvCheckSuccess.fields = vnL3StaticFields;
          break;
        case "L3VN_BGP4":
          this.csvCheckSuccess.fields = vnL3BGP4Fields;
          break;
        default:
          throw new Error(`unknown file type ${this.form.fileType}`);
      }

      if (this.csvCheckResponse.error === null) {
        /** CSVチェック成功時 */
        this.csvCheckSuccess.items =
          this.csvCheckResponse.success!.vnConnectCsvList;
        this.showCsvSuccess = true;
        this.showCsvError = false;
      } else if (this.csvCheckResponse.success === null) {
        /** CSVチェックエラー発生時 */
        this.csvCheckError.items = this.csvCheckResponse.error.checkErrorList;
        this.showCsvError = true;
        this.showCsvSuccess = false;
      }
    },
    async submit() {
      if (this.showCsvSuccess) {
        this.alertMsg = null;
        await this.$confirm(`こちらの内容で追加します。よろしいですか？`);
        await this.$api.vNConnect.registerVNConnectCsv({
          vnConnectCsvList: this.csvCheckResponse.success!.vnConnectCsvList,
          vnConnectCsvKeyId: this.csvCheckResponse.success!.vnConnectCsvKeyId,
        });
        (this.$refs.modal as typeof VueFinalModal & { ok: () => void }).ok();
      } else {
        this.showCsvSuccess = false;
        this.alertMsg = this.$filter.message("csv_no_select");
      }
    },
    /** tdにクラスをつける */
    cellClass(field: AppTableHeader<unknown>): string[] | undefined {
      if (field.key === "description") {
        return ["text-pre-wrap"];
      }
      return undefined;
    },
  },
});

/** ファイルチェック成功時表示する項目 */
const vnL2Fields = [
  { key: "enumber", title: "E番号", width: "140px" },
  { key: "siteName", title: "サイト名", width: "250px" },
  { key: "vlan", title: "VLAN", width: "140px" },
  { key: "vpnVnCode", title: "接続先VPN/VNコード", width: "200px" },
  { key: "vnConnectName", title: "VNコネクト名", width: "250px" },
  { key: "description", title: "備考", width: "300px" },
  { key: "bandwidthType", title: "品目", width: "200px" },
  { key: "bandwidth", title: "指定帯域", width: "200px" },
  { key: "qos", title: "QoS設定", width: "140px" },
  { key: "vlanControl", title: "VLAN制御", width: "140px" },
];
const vnL3BGP4Fields = [
  { key: "enumber", title: "E番号", width: "140px" },
  { key: "siteName", title: "サイト名", width: "250px" },
  { key: "vlan", title: "VLAN", width: "140px" },
  { key: "vpnVnCode", title: "接続先VPN/VNコード", width: "200px" },
  { key: "vnConnectName", title: "VNコネクト名", width: "250px" },
  { key: "description", title: "備考", width: "300px" },
  { key: "bandwidthType", title: "品目", width: "200px" },
  { key: "bandwidth", title: "指定帯域", width: "200px" },
  { key: "primaryAddress", title: "DHCPプライマリサーバ", width: "220px" },
  { key: "secondaryAddress", title: "DHCPセカンダリサーバ", width: "220px" },
  { key: "wanAddress", title: "WANアドレス", width: "220px" },
  { key: "asNumber", title: "AS番号", width: "140px" },
  { key: "peerIpAddress", title: "ピアIPアドレス", width: "220px" },
  { key: "md5Key", title: "MD5キー", width: "250px" },
  { key: "med", title: "MED", width: "140px" },
  { key: "localPreference", title: "Local Preference", width: "140px" },
  { key: "routeAggregation", title: "経路集約", width: "140px" },
  {
    key: "routeAggregationAddress1",
    title: "経路集約アドレス1",
    width: "220px",
  },
  {
    key: "routeAggregationAddress2",
    title: "経路集約アドレス2",
    width: "220px",
  },
  {
    key: "routeAggregationAddress3",
    title: "経路集約アドレス3",
    width: "220px",
  },
  {
    key: "routeAggregationAddress4",
    title: "経路集約アドレス4",
    width: "220px",
  },
  {
    key: "routeAggregationAddress5",
    title: "経路集約アドレス5",
    width: "220px",
  },
  { key: "qos", title: "QoS設定", width: "140px" },
  { key: "qosPattern", title: "割当帯域パターン", width: "160px" },
];

const vnL3StaticFields = [
  { key: "enumber", title: "E番号", width: "140px" },
  { key: "siteName", title: "サイト名", width: "250px" },
  { key: "vlan", title: "VLAN", width: "140px" },
  { key: "vpnVnCode", title: "接続先VPN/VNコード", width: "200px" },
  { key: "vnConnectName", title: "VNコネクト名", width: "250px" },
  { key: "description", title: "備考", width: "300px" },
  { key: "bandwidthType", title: "品目", width: "200px" },
  { key: "bandwidth", title: "指定帯域", width: "200px" },
  { key: "primaryAddress", title: "DHCPプライマリサーバ", width: "220px" },
  { key: "secondaryAddress", title: "DHCPセカンダリサーバ", width: "220px" },
  { key: "wanAddress", title: "WANアドレス", width: "220px" },
  { key: "lanAddress1", title: "LANアドレス1", width: "220px" },
  { key: "nextHopAddress1", title: "ネクストホップアドレス1", width: "220px" },
  { key: "lanAddress2", title: "LANアドレス2", width: "220px" },
  { key: "nextHopAddress2", title: "ネクストホップアドレス2", width: "220px" },
  { key: "lanAddress3", title: "LANアドレス3", width: "220px" },
  { key: "nextHopAddress3", title: "ネクストホップアドレス3", width: "220px" },
  { key: "lanAddress4", title: "LANアドレス4", width: "220px" },
  { key: "nextHopAddress4", title: "ネクストホップアドレス4", width: "220px" },
  { key: "lanAddress5", title: "LANアドレス5", width: "220px" },
  { key: "nextHopAddress5", title: "ネクストホップアドレス5", width: "220px" },
  { key: "qos", title: "QoS設定", width: "140px" },
  { key: "qosPattern", title: "割当帯域パターン", width: "160px" },
];
</script>
